import React, { Fragment, useState, useEffect, useCallback, useRef } from "react";
import {
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Button,
    Input,
    Form,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Label,
    FormGroup,
} from "reactstrap";
import DataTable from "react-data-table-component";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { ToastContainer, toast } from "react-toastify";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import AxiosInstance from "../../services/AxiosInstance";
import Spinner from "../../Components/Common/Spinner";
import NoDataFound from "../../Components/Common/NoDataFound";
import { customStyles } from "../../Constant";
import Swal from "sweetalert2";
import "react-toastify/dist/ReactToastify.css";

const PAGE_SIZES = [10, 25, 50, 100];

const RECRUITER_STATUS_OPTIONS = [
    { value: "BLOCKED", label: "Blocked" },
    { value: "PENDING", label: "Pending" },
    { value: "ACTIVE", label: "Active" },
    { value: "UNDER_REVIEW", label: "Under Review" },
];

const RECRUITER_ROLE_OPTIONS = [
    { value: "Recruiter", label: "Recruiter" },
    { value: "Lead Recruiter", label: "Lead Recruiter" },
];

const ManageRecruiters = () => {
    const [recruiters, setRecruiters] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchApplied, setSearchApplied] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [sortColumn, setSortColumn] = useState("created_at");
    const [sortDirection, setSortDirection] = useState("desc");
    const [updatingStatusId, setUpdatingStatusId] = useState(null);
    const [updatingRoleId, setUpdatingRoleId] = useState(null);
    const [inviteModalOpen, setInviteModalOpen] = useState(false);
    const [inviteEmail, setInviteEmail] = useState("");
    const [inviteCompany, setInviteCompany] = useState(null);
    const [companiesList, setCompaniesList] = useState([]);
    const [companiesLoading, setCompaniesLoading] = useState(false);
    const [sendingInvite, setSendingInvite] = useState(false);
    const companySearchTimeoutRef = useRef(null);

    const animatedComponents = makeAnimated();
    const selectColors = {
        primary: "#007bff",
        primary25: "#cce5ff",
        approved: "green",
        rejected: "red",
        hold: "orange",
        lightGray: "#f8f9fa",
        darkGray: "#343a40",
    };
    const getStatusColor = (value) => {
        switch (value) {
            case "ACTIVE": return selectColors.approved;
            case "PENDING": return selectColors.hold;
            case "UNDER_REVIEW": return selectColors.primary;
            case "BLOCKED": return selectColors.rejected;
            default: return selectColors.darkGray;
        }
    };
    const customStylesSelect = {
        control: (provided, state) => ({
            ...provided,
            minWidth: "140px",
            borderColor: state.isFocused ? selectColors.primary : provided.borderColor,
            boxShadow: state.isFocused ? `0 0 0 1px ${selectColors.primary}` : provided.boxShadow,
        }),
        singleValue: (provided, state) => ({
            ...provided,
            color: getStatusColor(state.data?.value),
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? getStatusColor(state.data.value) : state.isFocused ? selectColors.primary25 : selectColors.lightGray,
            color: state.isSelected || state.isFocused ? "#fff" : selectColors.darkGray,
        }),
    };

    const fetchRecruiters = useCallback(async (page, perPageSize, search, sortBy, sortDir) => {
        setLoading(true);
        setError(null);
        try {
            const res = await AxiosInstance().post("/admin/fetch_all_recruiters", {
                page,
                per_page: perPageSize,
                search: (search || "").trim() || undefined,
                sort_by: sortBy || "created_at",
                sort_dir: sortDir || "desc",
            });
            setRecruiters(Array.isArray(res.data?.data) ? res.data.data : []);
            setTotalRows(typeof res.data?.total === "number" ? res.data.total : 0);
        } catch (e) {
            console.error(e);
            toast.error(e.response?.data?.message || "Failed to load recruiters");
            setRecruiters([]);
            setTotalRows(0);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRecruiters(currentPage, perPage, searchApplied, sortColumn, sortDirection);
    }, [currentPage, perPage, searchApplied, sortColumn, sortDirection, fetchRecruiters]);

    const loadData = useCallback(() => {
        fetchRecruiters(currentPage, perPage, searchApplied, sortColumn, sortDirection);
    }, [fetchRecruiters, currentPage, perPage, searchApplied, sortColumn, sortDirection]);

    const formatDate = (dateStr) => {
        if (!dateStr) return "—";
        try {
            const d = new Date(dateStr);
            return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString();
        } catch {
            return dateStr;
        }
    };

    const handleStatusChange = async (recruiterId, newStatus) => {
        if (newStatus === "BLOCKED") {
            const result = await Swal.fire({
                title: "Block recruiter?",
                text: "Are you sure you want to block this recruiter? All jobs posted by this recruiter will be closed.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, block",
                cancelButtonText: "Cancel",
                confirmButtonColor: "#d33",
                reverseButtons: true,
            });
            if (!result.isConfirmed) {
                fetchRecruiters(currentPage, perPage, searchApplied, sortColumn, sortDirection);
                return;
            }
        }
        setUpdatingStatusId(recruiterId);
        try {
            const res = await AxiosInstance().post("/admin/update_recruiter_status", {
                recruiter_id: recruiterId,
                status: newStatus,
            });
            if (res.data.status) {
                toast.success(res.data.message || "Status updated");
                fetchRecruiters(currentPage, perPage, searchApplied, sortColumn, sortDirection);
            } else {
                toast.error(res.data.message || "Update failed");
            }
        } catch (e) {
            toast.error(e.response?.data?.message || "Update failed");
        } finally {
            setUpdatingStatusId(null);
        }
    };

    const handleRoleChange = async (recruiterId, newRole) => {
        if (!newRole) return;
        setUpdatingRoleId(recruiterId);
        try {
            const res = await AxiosInstance().post("/admin/update_recruiter_role", {
                recruiter_id: recruiterId,
                role: newRole,
            });
            if (res.data.status) {
                toast.success(res.data.message || "Role updated");
                fetchRecruiters(currentPage, perPage, searchApplied, sortColumn, sortDirection);
            } else {
                toast.error(res.data.message || "Update failed");
            }
        } catch (e) {
            toast.error(e.response?.data?.message || "Update failed");
        } finally {
            setUpdatingRoleId(null);
        }
    };

    const handleSearchSubmit = (e) => {
        e?.preventDefault();
        setSearchApplied(searchQuery);
        setCurrentPage(1);
    };

    const handlePageChange = (page) => setCurrentPage(page);
    const handlePerRowsChange = (newPerPage, page) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleSort = (selectedColumn, direction) => {
        const field = selectedColumn?.id || "created_at";
        setSortColumn(field);
        setSortDirection(direction === "asc" ? "asc" : "desc");
        setCurrentPage(1);
    };

    const fetchCompaniesForInvite = useCallback(async (search = "") => {
        setCompaniesLoading(true);
        try {
            const res = await AxiosInstance().post("/admin/fetch_all_companies", {
                page: 1,
                per_page: 50,
                search: search.trim() || undefined,
                sort_by: "company_name",
                sort_dir: "asc",
            });
            const data = res.data?.data ?? [];
            setCompaniesList(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error(e.response?.data?.message || "Failed to load companies");
            setCompaniesList([]);
        } finally {
            setCompaniesLoading(false);
        }
    }, []);

    useEffect(() => {
        if (inviteModalOpen) {
            fetchCompaniesForInvite("");
        }
    }, [inviteModalOpen, fetchCompaniesForInvite]);

    const handleCompanyInputChange = useCallback(
        (inputValue) => {
            if (companySearchTimeoutRef.current) {
                clearTimeout(companySearchTimeoutRef.current);
            }
            companySearchTimeoutRef.current = setTimeout(() => {
                fetchCompaniesForInvite(inputValue || "");
            }, 300);
        },
        [fetchCompaniesForInvite]
    );

    useEffect(() => {
        return () => {
            if (companySearchTimeoutRef.current) {
                clearTimeout(companySearchTimeoutRef.current);
            }
        };
    }, []);

    const openInviteModal = () => {
        setInviteEmail("");
        setInviteCompany(null);
        setInviteModalOpen(true);
    };

    const closeInviteModal = () => {
        setInviteModalOpen(false);
        setInviteEmail("");
        setInviteCompany(null);
    };

    const handleSendInvitation = async (e) => {
        e?.preventDefault();
        const email = inviteEmail.trim();
        const companyId = inviteCompany?.value ?? inviteCompany?.company_id;
        if (!email) {
            toast.error("Please enter an email address");
            return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(email)) {
            toast.error("Please enter a valid email address");
            return;
        }
        setSendingInvite(true);
        try {
            const res = await AxiosInstance().post("/admin/send_recruiter_invitation", {
                invite_email: email,
                company_id: companyId,
            });
            if (res.data.status) {
                toast.success(res.data.message || "Invitation sent successfully");
                closeInviteModal();
            } else {
                toast.error(res.data.message || "Failed to send invitation");
            }
        } catch (e) {
            toast.error(e.response?.data?.message || "Failed to send invitation");
        } finally {
            setSendingInvite(false);
        }
    };

    const companyOptions = companiesList.map((c) => ({
        value: c.company_id,
        label: c.company_name || "",
    }));

    const tableColumns = [
        {
            name: "#",
            cell: (row, index) => (currentPage - 1) * perPage + index + 1,
            width: "60px",
            sortable: false,
        },
        {
            id: "recruiter_full_name",
            name: "Name",
            selector: (row) => row.recruiter_full_name || "—",
            sortable: true,
            grow: 1.5,
            cell: (row) => <span className="fw-medium">{row.recruiter_full_name || "—"}</span>,
        },
        {
            id: "recruiter_email",
            name: "Email",
            selector: (row) => row.recruiter_email || "—",
            sortable: true,
            grow: 1.5,
        },
        {
            id: "company_name",
            name: "Company Name",
            selector: (row) => row.company_name || "—",
            sortable: true,
            grow: 1.5,
            cell: (row) => <span>{row.company_name || "—"}</span>,
        },
        {
            id: "recruiter_mobile_number",
            name: "Mobile",
            selector: (row) => row.recruiter_mobile_number || "—",
            sortable: true,
            width: "120px",
        },
        {
            id: "address",
            name: "Address",
            selector: (row) => row.address || "—",
            sortable: true,
            grow: 1.5,
        },
        {
            name: "Status",
            width: "190px",
            center: true,
            cell: (row) => {
                const currentStatus = (row.status || "").toUpperCase();
                const valueOption = RECRUITER_STATUS_OPTIONS.find((opt) => opt.value === currentStatus) || { value: currentStatus || "", label: currentStatus || "—" };
                return (
                    <Select
                        components={animatedComponents}
                        className="basic-single"
                        classNamePrefix="select"
                        value={valueOption}
                        onChange={(selectedOption) => selectedOption && handleStatusChange(row.recruiter_id, selectedOption.value)}
                        options={RECRUITER_STATUS_OPTIONS}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{
                            ...customStylesSelect,
                            menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
                        }}
                        isDisabled={updatingStatusId === row.recruiter_id}
                        isSearchable={false}
                        menuPlacement="auto"
                    />
                );
            },
        },
        {
            id: "role",
            name: "Role",
            selector: (row) => row.role || "—",
            sortable: true,
            width: "160px",
            center: true,
            cell: (row) => {
                const rawRole = row.role || "Recruiter";
                const currentRole = rawRole === "RECRUITER" ? "Recruiter" : rawRole;
                const valueOption = RECRUITER_ROLE_OPTIONS.find((opt) => opt.value === currentRole) || { value: currentRole, label: currentRole };
                return (
                    <Select
                        components={animatedComponents}
                        className="basic-single"
                        classNamePrefix="select"
                        value={valueOption}
                        onChange={(selectedOption) => selectedOption && handleRoleChange(row.recruiter_id, selectedOption.value)}
                        options={RECRUITER_ROLE_OPTIONS}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{
                            ...customStylesSelect,
                            menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
                        }}
                        isDisabled={updatingRoleId === row.recruiter_id}
                        isSearchable={false}
                        menuPlacement="auto"
                    />
                );
            },
        },
        {
            name: "Jobs",
            selector: (row) => row.jobs_count ?? "—",
            width: "80px",
            center: true,
        },
        {
            id: "created_at",
            name: "Joined",
            selector: (row) => row.created_at || "—",
            sortable: true,
            width: "110px",
            cell: (row) => <span className="text-muted small">{formatDate(row.created_at)}</span>,
        },
    ];

    if (loading && recruiters.length === 0) {
        return (
            <Fragment>
                <Breadcrumbs title="Recruiters" mainTitle="Admin" />
                <div className="d-flex justify-content-center align-items-center py-5">
                    <Spinner />
                </div>
            </Fragment>
        );
    }

    if (error && recruiters.length === 0) {
        return (
            <Fragment>
                <Breadcrumbs title="Recruiters" mainTitle="Admin" />
                <Row>
                    <Col xs="12">
                        <Card>
                            <CardBody className="text-center py-5">
                                <p className="text-danger mb-0">{error}</p>
                                <Button color="primary" className="mt-3" onClick={loadData}>
                                    Retry
                                </Button>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                <ToastContainer />
            </Fragment>
        );
    }

    return (
        <Fragment>
            <Breadcrumbs title="Recruiters" mainTitle="Admin" />
            <Row>
                <Col xs="12" className="box-col-12 xl-100">
                    <Card>
                        <CardHeader className="py-3">
                            <div className="d-flex flex-wrap justify-content-between align-items-end mb-3">
                                <Form
                                    className="theme-form me-2 flex-grow-1"
                                    onSubmit={handleSearchSubmit}
                                >
                                    <div
                                        className="input-group flex-nowrap"
                                        style={{ height: 35, maxWidth: "350px" }}
                                    >
                                        <Input
                                            className="form-control"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            type="search"
                                            placeholder="Search by name, email, company, mobile or address..."
                                        />
                                        <Button
                                            color="primary"
                                            type="submit"
                                            className="btn-primary input-group-text pe-3 ps-3"
                                        >
                                            Search
                                        </Button>
                                    </div>
                                </Form>
                                <div className="d-flex align-items-center gap-2 ms-auto mt-3 mt-md-0">
                                    <Button color="primary" onClick={openInviteModal} className="d-flex align-items-center">
                                        <i className="fa fa-envelope me-1" /> Send Invitation
                                    </Button>
                                    <Button color="primary" outline onClick={loadData} className="d-flex align-items-center">
                                        <i className="fa fa-sync-alt me-1" /> Refresh
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>

                        <CardBody className="px-0 py-3">
                            <div className="table-container">
                                <DataTable
                                    responsive
                                    customStyles={customStyles}
                                    data={recruiters}
                                    columns={tableColumns}
                                    striped
                                    pagination
                                    paginationServer
                                    sortServer
                                    onSort={handleSort}
                                    defaultSortFieldId="created_at"
                                    defaultSortAsc={false}
                                    paginationTotalRows={totalRows}
                                    paginationPerPage={perPage}
                                    paginationRowsPerPageOptions={PAGE_SIZES}
                                    onChangePage={handlePageChange}
                                    onChangeRowsPerPage={handlePerRowsChange}
                                    progressPending={loading}
                                    progressComponent={<Spinner />}
                                    noDataComponent={<NoDataFound message="No recruiters found" />}
                                />
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Modal isOpen={inviteModalOpen} toggle={closeInviteModal} size="md">
                <ModalHeader toggle={closeInviteModal}>Send Invitation</ModalHeader>
                <Form onSubmit={handleSendInvitation}>
                    <ModalBody>
                        <FormGroup>
                            <Label for="invite_email">Email</Label>
                            <Input
                                id="invite_email"
                                type="email"
                                placeholder="recruiter@example.com"
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                required
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="invite_company">Company</Label>
                            <Select
                                inputId="invite_company"
                                isClearable
                                options={companyOptions}
                                value={inviteCompany}
                                onChange={setInviteCompany}
                                onInputChange={handleCompanyInputChange}
                                filterOption={null}
                                isLoading={companiesLoading}
                                placeholder="Type to search company..."
                                noOptionsMessage={({ inputValue }) =>
                                    companiesLoading ? "Searching..." : inputValue ? "No companies found" : "Type to search"
                                }
                                styles={{
                                    control: (provided) => ({
                                        ...provided,
                                        minHeight: "38px",
                                    }),
                                }}
                            />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" type="button" onClick={closeInviteModal}>
                            Cancel
                        </Button>
                        <Button color="primary" type="submit" disabled={sendingInvite}>
                            {sendingInvite ? "Sending…" : "Send Invitation"}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>

            <ToastContainer />
        </Fragment>
    );
};

export default ManageRecruiters;
